import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Plus, Pencil, Trash2, Eye, EyeOff, Save, X, ExternalLink, FileText, Settings2, Tag, ChevronUp, ChevronDown } from 'lucide-react';

const TYPES_REPARATION = [
  'Ecran', 'Batterie', 'Connecteur de charge', 'Camera arriere', 'Camera avant',
  'Haut-parleur', 'Micro', 'Bouton power', 'Bouton volume', 'Vitre arriere',
  'Nappe Face ID', 'Carte mere', 'Diagnostique', 'Desoxydation', 'Autre'
];

const MARQUES = ['Apple', 'Samsung', 'Huawei', 'Xiaomi', 'Oppo', 'OnePlus', 'Google', 'Sony', 'Autre'];

// Mapping valeur backend FR -> suffixe de clé i18n (affichage uniquement, la valeur stockée reste FR)
const REPAIR_TYPE_KEYS = {
  'Ecran': 'ecran',
  'Batterie': 'batterie',
  'Connecteur de charge': 'connecteurCharge',
  'Camera arriere': 'cameraArriere',
  'Camera avant': 'cameraAvant',
  'Haut-parleur': 'hautParleur',
  'Micro': 'micro',
  'Bouton power': 'boutonPower',
  'Bouton volume': 'boutonVolume',
  'Vitre arriere': 'vitreArriere',
  'Nappe Face ID': 'nappeFaceId',
  'Carte mere': 'carteMere',
  'Diagnostique': 'diagnostique',
  'Desoxydation': 'desoxydation',
  'Autre': 'autre',
};

const ServicePricesPage = () => {
  const { api, user } = useAuth();
  const { t, formatCurrency } = useLanguage();
  const [tab, setTab] = useState('prices');
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ marque: '', modele: '', type_reparation: '', prix_min: '', prix_max: '', delai: '', visible: true, ordre: 0 });

  // Page content state
  const [content, setContent] = useState(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [contentSaving, setContentSaving] = useState(false);
  const [contentSaved, setContentSaved] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Affichage traduit des valeurs backend (repli sur la valeur brute si non mappée)
  const repairTypeLabel = (value) => {
    const k = REPAIR_TYPE_KEYS[value];
    return k ? t(`servicePrices.repairTypes.${k}`) : value;
  };
  const brandLabel = (value) => (value === 'Autre' ? t('servicePrices.repairTypes.autre') : value);

  const fetchPrices = useCallback(async () => {
    try {
      const res = await api.get('/service-prices');
      setPrices(res.data || []);
    } catch {}
    setLoading(false);
  }, [api]);

  const fetchContent = useCallback(async () => {
    setContentLoading(true);
    try {
      const res = await api.get('/page-content/tarifs');
      setContent(res.data || {});
    } catch (err) {
      console.error('fetchContent error:', err);
      // Load defaults if API fails
      setContent({
        title: 'Tarifs Reparation Telephone', title_accent: 'Cayenne',
        subtitle: 'Decouvrez nos tarifs de reparation pour iPhone, Samsung, Huawei et plus.',
        phone: '0694274051', phone_display: '0694 27 40 51',
        address: '102 Avenue General de Gaulle, Porte 2, 97300 Cayenne',
        hours: 'Lun-Ven 9h-19h, Sam 9h-13h', whatsapp: '594694274051',
        badges: [
          { label: 'Reparation Express', desc: 'En 30 min', icon: 'zap', color: 'amber' },
          { label: 'Garantie 6 mois', desc: 'Sur toutes nos reparations', icon: 'shield', color: 'blue' },
          { label: 'Pieces certifiees', desc: 'Qualite premium', icon: 'wrench', color: 'green' },
          { label: 'Devis gratuit', desc: 'Sans engagement', icon: 'star', color: 'purple' },
        ],
        faq: [],
        cta_title: 'Besoin d\'un devis ?', cta_description: '',
        footer_line1: '', footer_line2: '',
      });
    }
    setContentLoading(false);
  }, [api]);

  useEffect(() => { fetchPrices(); }, [fetchPrices]);
  useEffect(() => { if (tab === 'content') fetchContent(); }, [tab, fetchContent]);

  const resetForm = () => {
    setForm({ marque: '', modele: '', type_reparation: '', prix_min: '', prix_max: '', delai: '', visible: true, ordre: 0 });
    setEditing(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!form.marque || !form.type_reparation) return;
    const payload = { ...form, prix_min: parseFloat(form.prix_min) || 0, prix_max: parseFloat(form.prix_max) || 0, ordre: parseInt(form.ordre) || 0 };
    try {
      if (editing) {
        await api.put(`/service-prices/${editing}`, payload);
      } else {
        await api.post('/service-prices', payload);
      }
      resetForm();
      fetchPrices();
    } catch (err) { console.error(err); }
  };

  const handleEdit = (p) => {
    setForm({ marque: p.marque, modele: p.modele, type_reparation: p.type_reparation, prix_min: p.prix_min, prix_max: p.prix_max, delai: p.delai || '', visible: p.visible, ordre: p.ordre || 0 });
    setEditing(p.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('servicePrices.confirmDelete'))) return;
    await api.delete(`/service-prices/${id}`);
    fetchPrices();
  };

  const toggleVisibility = async (p) => {
    await api.put(`/service-prices/${p.id}`, { visible: !p.visible });
    fetchPrices();
  };

  const grouped = prices.reduce((acc, p) => {
    const key = p.marque || 'Autre';
    if (!acc[key]) acc[key] = [];
    acc[key].push(p);
    return acc;
  }, {});

  // Content editing helpers
  const updateContent = (key, value) => setContent(prev => ({ ...prev, [key]: value }));

  const updateFaq = (index, field, value) => {
    const faq = [...(content.faq || [])];
    faq[index] = { ...faq[index], [field]: value };
    setContent(prev => ({ ...prev, faq }));
  };

  const addFaq = () => {
    const faq = [...(content.faq || []), { question: '', answer: '' }];
    setContent(prev => ({ ...prev, faq }));
  };

  const removeFaq = (index) => {
    const faq = (content.faq || []).filter((_, i) => i !== index);
    setContent(prev => ({ ...prev, faq }));
  };

  const updateBadge = (index, field, value) => {
    const badges = [...(content.badges || [])];
    badges[index] = { ...badges[index], [field]: value };
    setContent(prev => ({ ...prev, badges }));
  };

  const addBadge = () => {
    const badges = [...(content.badges || []), { label: '', desc: '', icon: 'star', color: 'blue' }];
    setContent(prev => ({ ...prev, badges }));
  };

  const removeBadge = (index) => {
    const badges = (content.badges || []).filter((_, i) => i !== index);
    setContent(prev => ({ ...prev, badges }));
  };

  const saveContent = async () => {
    setContentSaving(true);
    setContentSaved(false);
    try {
      await api.put('/page-content/tarifs', content);
      setContentSaved(true);
      setTimeout(() => setContentSaved(false), 3000);
    } catch (err) { console.error(err); }
    setContentSaving(false);
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 py-6" data-testid="service-prices-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-black text-white">{t('servicePrices.pageTitle')}</h1>
            <p className="text-sm text-gray-500 mt-1">{t('servicePrices.pageSubtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            <a href="/tarifs" target="_blank" rel="noreferrer" className="px-3 py-2 rounded-xl bg-white/5 text-gray-400 text-xs font-semibold hover:bg-white/10 transition-colors flex items-center gap-1.5" data-testid="view-public-page">
              <ExternalLink className="w-3.5 h-3.5" /> {t('servicePrices.viewPublicPage')}
            </a>
            {tab === 'prices' && (
              <button onClick={() => { resetForm(); setShowForm(true); }} className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-500 transition-colors flex items-center gap-1.5" data-testid="add-price-btn">
                <Plus className="w-4 h-4" /> {t('servicePrices.add')}
              </button>
            )}
          </div>
        </div>

        {/* Tab switcher */}
        <div className="flex gap-1 mb-6 p-1 rounded-xl bg-white/[0.03] border border-white/5 w-fit" data-testid="tab-switcher">
          <button onClick={() => setTab('prices')} className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${tab === 'prices' ? 'bg-blue-600 text-white' : 'text-gray-500 hover:text-white hover:bg-white/5'}`} data-testid="tab-prices">
            <Tag className="w-3.5 h-3.5" /> {t('servicePrices.tabPrices')}
          </button>
          <button onClick={() => setTab('content')} className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${tab === 'content' ? 'bg-blue-600 text-white' : 'text-gray-500 hover:text-white hover:bg-white/5'}`} data-testid="tab-content">
            <Settings2 className="w-3.5 h-3.5" /> {t('servicePrices.tabContent')}
          </button>
        </div>

        {/* PRICES TAB */}
        {tab === 'prices' && (
          <>
            {showForm && (
              <div className="mb-6 p-5 rounded-2xl border border-white/10 bg-white/[0.03]" data-testid="price-form">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold text-white">{editing ? t('servicePrices.editPriceTitle') : t('servicePrices.newPriceTitle')}</h3>
                  <button onClick={resetForm} className="text-gray-500 hover:text-white"><X className="w-4 h-4" /></button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <label className="text-[10px] text-gray-500 uppercase mb-1 block">{t('servicePrices.brand')}</label>
                    <select value={form.marque} onChange={e => setForm({...form, marque: e.target.value})} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" data-testid="price-marque">
                      <option value="">{t('servicePrices.choose')}</option>
                      {MARQUES.map(m => <option key={m} value={m}>{brandLabel(m)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500 uppercase mb-1 block">{t('servicePrices.model')}</label>
                    <input value={form.modele} onChange={e => setForm({...form, modele: e.target.value})} placeholder={t('servicePrices.modelPlaceholder')} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder-gray-600" data-testid="price-modele" />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500 uppercase mb-1 block">{t('servicePrices.repairType')}</label>
                    <select value={form.type_reparation} onChange={e => setForm({...form, type_reparation: e.target.value})} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" data-testid="price-type">
                      <option value="">{t('servicePrices.choose')}</option>
                      {TYPES_REPARATION.map(rt => <option key={rt} value={rt}>{repairTypeLabel(rt)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500 uppercase mb-1 block">{t('servicePrices.delay')}</label>
                    <input value={form.delai} onChange={e => setForm({...form, delai: e.target.value})} placeholder={t('servicePrices.delayPlaceholder')} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder-gray-600" data-testid="price-delai" />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500 uppercase mb-1 block">{t('servicePrices.priceMin')}</label>
                    <input type="number" value={form.prix_min} onChange={e => setForm({...form, prix_min: e.target.value})} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" data-testid="price-min" />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500 uppercase mb-1 block">{t('servicePrices.priceMax')}</label>
                    <input type="number" value={form.prix_max} onChange={e => setForm({...form, prix_max: e.target.value})} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" data-testid="price-max" />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500 uppercase mb-1 block">{t('servicePrices.order')}</label>
                    <input type="number" value={form.ordre} onChange={e => setForm({...form, ordre: e.target.value})} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" data-testid="price-ordre" />
                  </div>
                  <div className="flex items-end gap-2">
                    <label className="flex items-center gap-2 cursor-pointer py-2">
                      <input type="checkbox" checked={form.visible} onChange={e => setForm({...form, visible: e.target.checked})} className="rounded" data-testid="price-visible" />
                      <span className="text-xs text-gray-400">{t('servicePrices.visiblePublic')}</span>
                    </label>
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <button onClick={handleSave} className="px-5 py-2 rounded-xl bg-green-600 text-white text-sm font-semibold hover:bg-green-500 transition-colors flex items-center gap-1.5" data-testid="price-save-btn">
                    <Save className="w-4 h-4" /> {editing ? t('servicePrices.btnUpdate') : t('servicePrices.btnSave')}
                  </button>
                </div>
              </div>
            )}

            {loading ? (
              <div className="text-center py-16 text-gray-600">{t('servicePrices.loading')}</div>
            ) : prices.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-gray-500 text-sm">{t('servicePrices.noPrices')}</p>
                <p className="text-gray-700 text-xs mt-1">{t('servicePrices.noPricesHint')}</p>
              </div>
            ) : (
              <div className="space-y-6">
                {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([marque, items]) => (
                  <div key={marque} data-testid={`brand-${marque}`}>
                    <h3 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                      {brandLabel(marque)} <span className="text-gray-600 font-normal">({items.length})</span>
                    </h3>
                    <div className="rounded-xl border border-white/5 overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-[10px] uppercase text-gray-600 border-b border-white/5">
                            <th className="px-4 py-2 text-left">{t('servicePrices.model')}</th>
                            <th className="px-4 py-2 text-left">{t('servicePrices.colRepair')}</th>
                            <th className="px-4 py-2 text-right">{t('servicePrices.colPrice')}</th>
                            <th className="px-4 py-2 text-center">{t('servicePrices.delay')}</th>
                            <th className="px-4 py-2 text-center">{t('servicePrices.colVisible')}</th>
                            <th className="px-4 py-2 text-right">{t('servicePrices.colActions')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map(p => (
                            <tr key={p.id} className="border-b border-white/[0.02] hover:bg-white/[0.02] transition-colors" data-testid={`price-row-${p.id}`}>
                              <td className="px-4 py-2.5 text-white font-medium">{p.modele || <span className="text-gray-600">{t('servicePrices.allModels')}</span>}</td>
                              <td className="px-4 py-2.5 text-gray-300">{repairTypeLabel(p.type_reparation)}</td>
                              <td className="px-4 py-2.5 text-right font-mono text-green-400">
                                {p.prix_min === p.prix_max ? formatCurrency(p.prix_min) : `${formatCurrency(p.prix_min)} - ${formatCurrency(p.prix_max)}`}
                              </td>
                              <td className="px-4 py-2.5 text-center text-gray-500">{p.delai || '-'}</td>
                              <td className="px-4 py-2.5 text-center">
                                <button onClick={() => toggleVisibility(p)} className="mx-auto" data-testid={`toggle-vis-${p.id}`}>
                                  {p.visible ? <Eye className="w-4 h-4 text-green-400" /> : <EyeOff className="w-4 h-4 text-gray-600" />}
                                </button>
                              </td>
                              <td className="px-4 py-2.5 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <button onClick={() => handleEdit(p)} className="p-1.5 rounded-lg hover:bg-white/10 text-gray-500 hover:text-blue-400 transition-colors" data-testid={`edit-price-${p.id}`}>
                                    <Pencil className="w-3.5 h-3.5" />
                                  </button>
                                  <button onClick={() => handleDelete(p.id)} className="p-1.5 rounded-lg hover:bg-white/10 text-gray-500 hover:text-red-400 transition-colors" data-testid={`delete-price-${p.id}`}>
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* CONTENT TAB */}
        {tab === 'content' && (
          (!content || contentLoading) ? (
            <div className="text-center py-16 text-gray-600">{t('servicePrices.contentLoading')}</div>
          ) : (
            <div className="space-y-6" data-testid="page-content-editor">
              {/* Info banner */}
              <div className="p-3 rounded-xl bg-blue-500/10 border border-blue-500/20 text-xs text-blue-300">
                {t('servicePrices.contentBannerPre')} <strong>/tarifs</strong>{t('servicePrices.contentBannerPost')}
              </div>

              {/* Title & subtitle */}
              <div className="p-5 rounded-2xl border border-white/10 bg-white/[0.03]">
                <h3 className="text-sm font-bold text-white mb-4">{t('servicePrices.headerSection')}</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] text-gray-500 uppercase mb-1 block">{t('servicePrices.mainTitle')}</label>
                    <input value={content.title || ''} onChange={e => updateContent('title', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder-gray-600" data-testid="content-title" />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500 uppercase mb-1 block">{t('servicePrices.accentWord')}</label>
                    <input value={content.title_accent || ''} onChange={e => updateContent('title_accent', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder-gray-600" data-testid="content-title-accent" />
                  </div>
                </div>
                <div className="mt-3">
                  <label className="text-[10px] text-gray-500 uppercase mb-1 block">{t('servicePrices.seoDescription')}</label>
                  <textarea value={content.subtitle || ''} onChange={e => updateContent('subtitle', e.target.value)} rows={2} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder-gray-600" data-testid="content-subtitle" />
                </div>
              </div>

              {/* Contact Info */}
              <div className="p-5 rounded-2xl border border-white/10 bg-white/[0.03]">
                <h3 className="text-sm font-bold text-white mb-4">{t('servicePrices.contactInfo')}</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-[10px] text-gray-500 uppercase mb-1 block">{t('servicePrices.phone')}</label>
                    <input value={content.phone || ''} onChange={e => updateContent('phone', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder-gray-600" data-testid="content-phone" />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500 uppercase mb-1 block">{t('servicePrices.phoneDisplay')}</label>
                    <input value={content.phone_display || ''} onChange={e => updateContent('phone_display', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder-gray-600" data-testid="content-phone-display" />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500 uppercase mb-1 block">{t('servicePrices.whatsappLabel')}</label>
                    <input value={content.whatsapp || ''} onChange={e => updateContent('whatsapp', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder-gray-600" data-testid="content-whatsapp" />
                  </div>
                  <div className="col-span-2">
                    <label className="text-[10px] text-gray-500 uppercase mb-1 block">{t('servicePrices.address')}</label>
                    <input value={content.address || ''} onChange={e => updateContent('address', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder-gray-600" data-testid="content-address" />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500 uppercase mb-1 block">{t('servicePrices.hours')}</label>
                    <input value={content.hours || ''} onChange={e => updateContent('hours', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder-gray-600" data-testid="content-hours" />
                  </div>
                </div>
              </div>

              {/* Trust Badges */}
              <div className="p-5 rounded-2xl border border-white/10 bg-white/[0.03]">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold text-white">{t('servicePrices.trustBadges')}</h3>
                  <button onClick={addBadge} className="px-3 py-1 rounded-lg bg-blue-600/20 text-blue-400 text-[10px] font-semibold hover:bg-blue-600/30 transition-colors" data-testid="add-badge-btn">
                    {t('servicePrices.addShort')}
                  </button>
                </div>
                <div className="space-y-3">
                  {(content.badges || []).map((b, i) => (
                    <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end" data-testid={`badge-row-${i}`}>
                      <div>
                        <label className="text-[10px] text-gray-500 uppercase mb-1 block">{t('servicePrices.titleField')}</label>
                        <input value={b.label || ''} onChange={e => updateBadge(i, 'label', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
                      </div>
                      <div>
                        <label className="text-[10px] text-gray-500 uppercase mb-1 block">{t('servicePrices.description')}</label>
                        <input value={b.desc || ''} onChange={e => updateBadge(i, 'desc', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
                      </div>
                      <button onClick={() => removeBadge(i)} className="p-2 rounded-lg hover:bg-red-500/10 text-gray-600 hover:text-red-400 transition-colors" data-testid={`remove-badge-${i}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* FAQ */}
              <div className="p-5 rounded-2xl border border-white/10 bg-white/[0.03]">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold text-white">{t('servicePrices.faqTitle')}</h3>
                  <button onClick={addFaq} className="px-3 py-1 rounded-lg bg-blue-600/20 text-blue-400 text-[10px] font-semibold hover:bg-blue-600/30 transition-colors" data-testid="add-faq-btn">
                    {t('servicePrices.addShort')}
                  </button>
                </div>
                <div className="space-y-4">
                  {(content.faq || []).map((f, i) => (
                    <div key={i} className="p-3 rounded-xl bg-white/[0.02] border border-white/5" data-testid={`faq-row-${i}`}>
                      <div className="flex items-start gap-2">
                        <div className="flex-1 space-y-2">
                          <div>
                            <label className="text-[10px] text-gray-500 uppercase mb-1 block">{t('servicePrices.question')}</label>
                            <input value={f.question || ''} onChange={e => updateFaq(i, 'question', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
                          </div>
                          <div>
                            <label className="text-[10px] text-gray-500 uppercase mb-1 block">{t('servicePrices.answer')}</label>
                            <textarea value={f.answer || ''} onChange={e => updateFaq(i, 'answer', e.target.value)} rows={2} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
                          </div>
                        </div>
                        <button onClick={() => removeFaq(i)} className="p-2 rounded-lg hover:bg-red-500/10 text-gray-600 hover:text-red-400 transition-colors mt-5" data-testid={`remove-faq-${i}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA Section */}
              <div className="p-5 rounded-2xl border border-white/10 bg-white/[0.03]">
                <h3 className="text-sm font-bold text-white mb-4">{t('servicePrices.ctaSection')}</h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-[10px] text-gray-500 uppercase mb-1 block">{t('servicePrices.ctaTitle')}</label>
                    <input value={content.cta_title || ''} onChange={e => updateContent('cta_title', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" data-testid="content-cta-title" />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500 uppercase mb-1 block">{t('servicePrices.ctaDescription')}</label>
                    <textarea value={content.cta_description || ''} onChange={e => updateContent('cta_description', e.target.value)} rows={2} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" data-testid="content-cta-desc" />
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="p-5 rounded-2xl border border-white/10 bg-white/[0.03]">
                <h3 className="text-sm font-bold text-white mb-4">{t('servicePrices.footer')}</h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-[10px] text-gray-500 uppercase mb-1 block">{t('servicePrices.line1')}</label>
                    <input value={content.footer_line1 || ''} onChange={e => updateContent('footer_line1', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" data-testid="content-footer1" />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500 uppercase mb-1 block">{t('servicePrices.line2')}</label>
                    <input value={content.footer_line2 || ''} onChange={e => updateContent('footer_line2', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" data-testid="content-footer2" />
                  </div>
                </div>
              </div>

              {/* Save + Preview toggle */}
              <div className="flex items-center justify-between gap-3">
                <button onClick={() => setShowPreview(!showPreview)} className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors flex items-center gap-1.5 ${showPreview ? 'bg-purple-600 text-white' : 'bg-white/5 text-gray-400 hover:text-white hover:bg-white/10'}`} data-testid="toggle-preview">
                  <Eye className="w-4 h-4" /> {showPreview ? t('servicePrices.hidePreview') : t('servicePrices.livePreview')}
                </button>
                <div className="flex items-center gap-3">
                  {contentSaved && <span className="text-xs text-green-400 font-semibold">{t('servicePrices.savedSuccess')}</span>}
                  <button onClick={saveContent} disabled={contentSaving} className="px-6 py-2.5 rounded-xl bg-green-600 text-white text-sm font-semibold hover:bg-green-500 transition-colors flex items-center gap-1.5 disabled:opacity-50" data-testid="save-content-btn">
                    <Save className="w-4 h-4" /> {contentSaving ? t('servicePrices.saving') : t('servicePrices.saveContent')}
                  </button>
                </div>
              </div>

              {/* Live Preview */}
              {showPreview && content && (
                <div className="mt-4 p-6 rounded-2xl border border-purple-500/20 bg-gray-950" data-testid="content-preview">
                  <div className="text-center mb-3">
                    <span className="text-[10px] font-bold text-purple-400 uppercase tracking-widest">{t('servicePrices.previewLabel')}</span>
                  </div>
                  <div className="border border-white/5 rounded-xl overflow-hidden">
                    {/* Preview header */}
                    <div className="p-4 border-b border-white/5" style={{background: 'linear-gradient(180deg, rgba(124,58,237,0.06) 0%, transparent 100%)'}}>
                      <h1 className="text-xl font-black text-white">{content.title} <span className="text-purple-400">{content.title_accent}</span></h1>
                      <p className="text-xs text-gray-400 mt-1">{content.subtitle}</p>
                      <div className="flex gap-3 mt-2 text-[10px] text-gray-500">
                        <span>{content.address}</span>
                        <span>{content.hours}</span>
                      </div>
                    </div>
                    {/* Preview badges */}
                    <div className="grid grid-cols-4 gap-2 p-3">
                      {(content.badges || []).map((b, i) => (
                        <div key={i} className="p-2 rounded-lg border border-white/5 bg-white/[0.02] text-center">
                          <p className="text-[10px] font-bold text-white">{b.label}</p>
                          <p className="text-[8px] text-gray-600">{b.desc}</p>
                        </div>
                      ))}
                    </div>
                    {/* Preview FAQ */}
                    {(content.faq || []).length > 0 && (
                      <div className="p-3 border-t border-white/5">
                        <p className="text-[10px] font-bold text-white mb-2">{t('servicePrices.faqShort')}</p>
                        {(content.faq || []).map((f, i) => (
                          <div key={i} className="mb-2 p-2 rounded-lg bg-white/[0.01]">
                            <p className="text-[10px] font-semibold text-white">{f.question}</p>
                            <p className="text-[9px] text-gray-500 mt-0.5">{f.answer}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Preview CTA */}
                    <div className="p-3 border-t border-white/5 text-center">
                      <p className="text-xs font-bold text-white">{content.cta_title}</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">{content.cta_description}</p>
                      <div className="flex justify-center gap-2 mt-2">
                        <span className="px-3 py-1 rounded-lg bg-purple-600/30 text-purple-300 text-[9px] font-semibold">{content.phone_display}</span>
                        <span className="px-3 py-1 rounded-lg bg-green-500/10 text-green-400 text-[9px] font-semibold">WhatsApp</span>
                      </div>
                    </div>
                    {/* Preview footer */}
                    <div className="p-2 border-t border-white/5 text-center">
                      <p className="text-[8px] text-gray-600">{content.footer_line1}</p>
                      <p className="text-[8px] text-gray-700">{content.footer_line2}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )
        )}
      </div>
    </Layout>
  );
};

export default ServicePricesPage;
